// src/context/SubscriptionContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, increment, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState({
    status: 'inactive',
    planType: null,
    expiryDate: null,
    downloadCount: 0,
    maxDownloads: 3,
    isLoading: true
  });

  // Set up a real-time listener for the user document
  useEffect(() => {
    if (!user) {
      setSubscription({
        status: 'inactive',
        planType: null,
        expiryDate: null,
        downloadCount: 0,
        maxDownloads: 3,
        isLoading: false
      });
      return;
    }

    // Initialize user document if it doesn't exist
    const initializeUserDocument = async () => {
      try {
        const userDocRef = doc(db, 'users', user.uid);        
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          // Create the user document if it doesn't exist
          const newUserData = {
            userId: user.uid,
            email: user.email,
            subscriptionStatus: 'inactive',
            downloadCount: 0,
            maxDownloads: 3,
            createdAt: new Date()
          };
          await setDoc(userDocRef, newUserData);
        } else if (userDoc.data().downloadCount === undefined) {          
          // Add the download count field if it doesn't exist
          await updateDoc(userDocRef, {
            downloadCount: 0,
            maxDownloads: 3
          });
        } else {
          console.log("Existing user document found with data");
        }
      } catch (error) {
        console.error("❌ Error initializing user document:", error);
      }
    };

    // Initialize first, then set up listener
    initializeUserDocument().then(() => {
      // Set up real-time listener for user document
      const userDocRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (doc) => {
        if (doc.exists()) {
          const userData = doc.data();          
          setSubscription({
            status: userData.subscriptionStatus || 'inactive',
            planType: userData.planType || 'free',
            expiryDate: userData.expiryDate,
            downloadCount: typeof userData.downloadCount === 'number' ? userData.downloadCount : 0,
            maxDownloads: typeof userData.maxDownloads === 'number' ? userData.maxDownloads : 3,
            isLoading: false
          });
        } else {
          console.log(`⚠️ No document exists at users/${user.uid} in real-time listener`);
        }
      }, (error) => {
        console.error("❌ Error in real-time listener:", error);
        setSubscription(prev => ({...prev, isLoading: false}));
      });
      
      // Clean up listener on unmount
      return () => {
        unsubscribe();
      };
    });
  }, [user]);

  const isSubscriptionActive = () => {
    // Check if subscription is active and not expired
    if (subscription.status !== 'active') return false;
    if (subscription.planType === 'lifetime') return true;
    
    if (subscription.expiryDate) {
      const expiryDate = subscription.expiryDate instanceof Date 
        ? subscription.expiryDate 
        : new Date(subscription.expiryDate.seconds * 1000);
      return expiryDate > new Date();
    }
    
    return true;
  };

  const canDownloadResume = async () => {
    // If not logged in, can't download
    if (!user) {
      console.log("Download denied: No user logged in");
      return false;
    }
    
    // If subscription is active or admin, user can download
    if (isSubscriptionActive() || (user.email === process.env.REACT_APP_ADMIN_EMAIL)) {
      console.log(`Download approved: User has ${isSubscriptionActive() ? 'active subscription' : 'admin privileges'}`);
      return true;
    }
    
    try {
      // Always get the latest data from the database
      const userDocRef = doc(db, 'users', user.uid);
      
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.error(`❌ User document not found at users/${user.uid}`);
        return false;
      }
      
      const userData = userDoc.data();
      
      // Get download count and max downloads
      const downloadCount = userData.downloadCount || 0;
      const maxDownloads = userData.maxDownloads || 3;
      
      // If user has free downloads remaining, they can download
      if (downloadCount < maxDownloads) {
        // Increment the download counter in the database
        try {          
          const updateData = {
            downloadCount: increment(1),
            lastDownloadAt: new Date()
          };
          
          await updateDoc(userDocRef, updateData);          
          
          // Verify the update
          const updatedDoc = await getDoc(userDocRef);
          const updatedData = updatedDoc.data();
          
          return true;
        } catch (error) {
          console.error("❌ Error incrementing download count:", error);
          return false;
        }
      }      
      console.log("❌ Download denied: No downloads remaining");
      return false;
    } catch (error) {
      console.error("❌ Error checking download eligibility:", error);
      return false;
    }
  };

  const getFreeDownloadsRemaining = async () => {
    if (!user) { 
      return 0;
    }
    
    try {
      // Always fetch the latest data from the database 
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) { 
        return 3; // Default for new users
      }
      
      const userData = userDoc.data(); 
      
      const downloadCount = userData.downloadCount || 0;
      const maxDownloads = userData.maxDownloads || 3;
      const remaining = Math.max(0, maxDownloads - downloadCount); 
      
      return remaining;
    } catch (error) {
      console.error("❌ Error getting remaining downloads:", error);
      return 0;
    }
  };

  const value = {
    subscription,
    isSubscriptionActive,
    canDownloadResume,
    getFreeDownloadsRemaining
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};